import { useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";

// Product-page Technology carousel: black-background feature cards with a
// title and short description below each card. Matches PING.com's product
// page pattern. Renders 3 slides at a time on desktop, 1 on mobile, with
// horizontal scroll-snap + prev/next arrow buttons.
export default function TechCarousel({ slides }) {
  const scrollerRef = useRef(null);
  const [scrollIdx, setScrollIdx] = useState(0);
  if (!slides || slides.length === 0) return null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-tech-card]");
    const step = card ? card.offsetWidth + 20 : el.clientWidth;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
    setScrollIdx((i) => Math.max(0, Math.min(slides.length - 1, i + dir)));
  };

  return (
    <section className="container-ping pt-12 pb-8" data-testid="tech-carousel-section">
      <div className="flex items-end justify-between mb-5">
        <h2 className="heading-3">Technology</h2>
        {slides.length > 3 && (
          <div className="hidden md:flex gap-2" data-testid="tech-carousel-controls">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              disabled={scrollIdx === 0}
              className="h-10 w-16 border border-[#0A0A0A] rounded-full flex items-center justify-center btn-transition hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#0A0A0A]"
              aria-label="Previous slide"
              data-testid="tech-prev-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              disabled={scrollIdx >= slides.length - 3}
              className="h-10 w-16 border border-[#0A0A0A] rounded-full flex items-center justify-center btn-transition hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#0A0A0A]"
              aria-label="Next slide"
              data-testid="tech-next-btn"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-2 px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="tech-carousel-scroller"
      >
        {slides.map((s, i) => (
          <div
            key={i}
            data-tech-card
            className="snap-start shrink-0 basis-full md:basis-[calc((100%-2.5rem)/3)]"
            data-testid={`tech-slide-${i}`}
          >
            <div className="aspect-[16/10] bg-[#0A0A0A] overflow-hidden flex items-center justify-center">
              {s.image && (
                <img
                  src={s.image}
                  alt={s.title || "Technology"}
                  className="w-full h-full object-contain"
                  data-testid={`tech-slide-image-${i}`}
                />
              )}
            </div>
            <h3 className="mt-5 label-caps text-[#0A0A0A]" data-testid={`tech-slide-title-${i}`}>{s.title}</h3>
            {s.description && (
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed" data-testid={`tech-slide-description-${i}`}>{s.description}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
